import axios from 'axios'
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { addRequestBreadcrumb } from '@/lib/observability'
import { PRESENCE_HEADER, installPresenceTracking, presenceState } from '@/lib/presence'

/* ------------------------------------------------------------------
   کلاینت API — سند مادر بند ۶.۱ و ۱۵
   ------------------------------------------------------------------
   قواعد اجباری:
   · احراز هویت فقط با Cookie امن HttpOnly/SameSite است.
     ❌ هیچ Token در localStorage ذخیره نمی‌شود (شکاف SG-04).
   · Correlation-ID از ورودی تا Queue حفظ می‌شود (بند ۱۵).
   · Idempotency-Key روی Commandهای حساس (بند ۱۵).
   · Envelope خطا دقیقاً مطابق بند ۱۵.۱ است.
   ------------------------------------------------------------------ */

/** ساختار خطای استاندارد Backend — بند ۱۵.۱ */
export interface ApiErrorPayload {
  code: string
  message: string
  fields?: Array<{ field: string; code: string; message?: string }>
  request_id?: string
}

export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly fields: Array<{ field: string; code: string; message?: string }>
  readonly requestId: string | null

  constructor(status: number, payload: Partial<ApiErrorPayload>) {
    super(payload.message || 'خطای نامشخص در ارتباط با سرور')
    this.name = 'ApiError'
    this.status = status
    this.code = payload.code || 'UNKNOWN_ERROR'
    this.fields = payload.fields ?? []
    this.requestId = payload.request_id ?? null
  }

  /** خطاهای اعتبارسنجی را به شکل قابل مصرف react-hook-form برمی‌گرداند */
  get fieldErrors(): Record<string, string> {
    return Object.fromEntries(
      this.fields.map((item) => [item.field, item.message || item.code]),
    )
  }

  get isValidation(): boolean {
    return this.status === 422
  }

  get isUnauthenticated(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }
}

/* ------------------------------------------------------------------ */

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(^|;\\s*)${name}=([^;]*)`))
  return match ? decodeURIComponent(match[2]) : null
}

/* ------------------------------------------------------------------ */

export const api: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  // ⬅ کلید کار با Session کوکی‌محور
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  timeout: 30_000,
})

/** Callback هنگام ابطال Session — توسط AuthProvider ست می‌شود */
let onUnauthenticated: (() => void) | null = null

export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler
}

/* ---------------- درخواست ---------------- */

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // ردیابی سراسری — بند ۱۵
  const correlationId = uuid()
  config.headers.set('X-Correlation-ID', correlationId)

  // همان شناسه به‌صورت Breadcrumb ثبت می‌شود؛ اگر بعداً خطایی رخ دهد،
  // گزارش Frontend و لاگ Backend از یک نخ آویزانند (بند ۱۶).
  addRequestBreadcrumb(config.method, config.url, correlationId)

  // CSRF — Sanctum کوکی XSRF-TOKEN را ست می‌کند
  const csrf = readCookie('XSRF-TOKEN')
  if (csrf) config.headers.set('X-XSRF-TOKEN', csrf)

  // 🔴 «آنلاین روی سایت» — درخواستِ پس‌زمینه (زنگوله، فهرست‌های چت) کسی را
  // آنلاین نگه نمی‌دارد؛ سرور `idle` را ثبت نمی‌کند. توضیح در `lib/presence.ts`.
  config.headers.set(PRESENCE_HEADER, presenceState())

  return config
})

installPresenceTracking()

/* ---------------- پاسخ ---------------- */

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ error?: ApiErrorPayload }>) => {
    // خطای شبکه یا timeout — سروری در کار نبوده که Envelope بدهد
    if (!error.response) {
      return Promise.reject(
        new ApiError(0, {
          code: 'NETWORK_ERROR',
          message: 'ارتباط با سرور برقرار نشد. اتصال اینترنت را بررسی کنید.',
        }),
      )
    }

    const { status, data } = error.response
    const apiError = new ApiError(status, data?.error ?? {})

    // Session باطل شده — کاربر باید دوباره وارد شود (AC-03)
    if (apiError.isUnauthenticated) onUnauthenticated?.()

    return Promise.reject(apiError)
  },
)

/* ------------------------------------------------------------------
   کمکی‌ها
   ------------------------------------------------------------------ */

/** پوشش پاسخ موفق — بند ۱۵: Envelope ثابت */
export interface ApiResponse<T> {
  data: T
  meta?: Record<string, unknown>
}

export interface PaginatedMeta {
  page: number
  page_size: number
  total: number
  last_page: number
}

export interface PaginatedResponse<T> {
  data: T[]
  meta: PaginatedMeta
}

/** Idempotency-Key برای Commandهای حساس — بند ۱۵ */
export function idempotent(key: string = uuid()) {
  return { headers: { 'Idempotency-Key': key } }
}

/** درخواست کوکی CSRF پیش از نخستین POST — الزام Sanctum SPA */
export async function ensureCsrfCookie(): Promise<void> {
  await axios.get('/sanctum/csrf-cookie', { withCredentials: true })
}
